import {
  Bone,
  CardSkeleton,
  HeaderSkeleton,
  Line,
  pick,
  placeholderKeys,
  SkeletonRegion,
} from '@ems/ui/patterns/skeleton-blocks'
import { StatCardSkeleton } from '@ems/ui/patterns/stat-card'

const STAT_KEYS = placeholderKeys('stat', 4)
const BAR_KEYS = placeholderKeys('bar', 3)
const ROW_KEYS = placeholderKeys('row', 5)
const ROW_WIDTHS = ['w-48', 'w-40', 'w-52', 'w-36', 'w-44'] as const

function PanelBones({ rows, title }: { rows: readonly string[]; title: string }) {
  return (
    <CardSkeleton className="gap-4 p-5">
      <div className="flex flex-col gap-1.5">
        <Line size="base" width={title} />
        <Line size="xs" width="w-40" />
      </div>
      <ul className="flex flex-col gap-4">
        {rows.map((key, at) => (
          <li key={key} className="flex items-center justify-between gap-3">
            <Line width={pick(ROW_WIDTHS, at)} />
            <Line width="w-16" />
          </li>
        ))}
      </ul>
    </CardSkeleton>
  )
}

/**
 * The dashboard before the manifest arrives. The person's cards are not known yet, so it shows
 * the full layout: four figures, the chart beside the queue, the ticket sales beside the newest
 * registrations, and the closed modules panel. Each part sits where the real one does, so the
 * page does not jump.
 */
export function DashboardSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-56" description="w-40" />
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {STAT_KEYS.map((key) => (
          <li key={key} className="flex *:w-full">
            <StatCardSkeleton />
          </li>
        ))}
      </ul>
      <div className="grid items-start gap-4 lg:grid-cols-3">
        <CardSkeleton className="gap-4 p-5 lg:col-span-2">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-1.5">
              <Line size="base" width="w-48" />
              <Line size="xs" width="w-64" />
            </div>
            <Bone className="h-8 w-64 rounded-md" />
          </div>
          <Bone className="h-56 w-full rounded-lg" />
        </CardSkeleton>
        <PanelBones rows={BAR_KEYS.slice(0, 2)} title="w-36" />
      </div>
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <CardSkeleton className="gap-5 p-5">
          <div className="flex flex-col gap-1.5">
            <Line size="base" width="w-32" />
            <Line size="xs" width="w-48" />
          </div>
          {BAR_KEYS.map((key, at) => (
            <div key={key} className="flex flex-col gap-2">
              <Line width={pick(ROW_WIDTHS, at)} />
              <Bone className="h-2 w-full rounded-full" />
            </div>
          ))}
        </CardSkeleton>
        <PanelBones rows={ROW_KEYS} title="w-44" />
      </div>
      <CardSkeleton className="gap-1.5 px-5 py-4">
        <Line size="base" width="w-56" />
        <Line size="xs" width="w-40" />
      </CardSkeleton>
    </SkeletonRegion>
  )
}
